/**
 * MarketContextWidget
 *
 * A 3-sensor cockpit dashboard widget featuring:
 * - Card 1: FearGauge (VIX semicircle gauge with status)
 * - Card 2: Market Trend (20-day sparkline + SMA-200 overlay)
 * - Card 3: SectorRotation (11 GICS sectors diverging bar chart)
 *
 * The regime is calculated and passed up to the parent Dashboard
 * which renders the CompassBanner above the Opportunity Scanner.
 *
 * Uses a 5-regime MECE classification system.
 */

import React, { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';
import FearGauge from './FearGauge';
import MarketMomentum from './MarketMomentum';
import SectorRotation from './SectorRotation';
import { calculateRegime, MarketInputs, RegimeInfo } from './utils';
import './MarketContextWidget.css';

interface VixData {
  status: string;
  current_vix?: number;
  week_52_high?: number;
  week_52_low?: number;
  percentile?: number;
  regime?: string;
}

interface SpyTrendData {
  status: string;
  symbol?: string;
  current_price?: number;
  sma_200?: number;
  trend?: 'bullish' | 'bearish' | 'neutral';
  percent_above_sma?: number;
  sma_200_slope?: number | null;
  slope_direction?: 'up' | 'down' | 'flat' | null;
}

interface MarketContextWidgetProps {
  /**
   * Fired whenever the regime changes. Parent gets the full RegimeInfo.
   */
  onRegimeChange?: (regime: RegimeInfo) => void;
  vertical?: boolean;
}

// Export RegimeInfo type for use by Dashboard
export type { RegimeInfo } from './utils';

const MarketContextWidget: React.FC<MarketContextWidgetProps> = ({ onRegimeChange, vertical = false }) => {
  const [vixData, setVixData] = useState<VixData | null>(null);
  const [spyTrend, setSpyTrend] = useState<SpyTrendData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMarketData = async () => {
      setLoading(true);
      try {
        const [vixResponse, spyResponse] = await Promise.all([
          apiService.getVixPercentile(),
          apiService.getSpyTrend()
        ]);
        setVixData(vixResponse);
        setSpyTrend(spyResponse);
      } catch (error) {
        console.error('Failed to fetch market data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();

    // Refresh every 5 minutes
    const interval = setInterval(fetchMarketData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate regime from market data
  const getRegime = (): RegimeInfo => {
    if (!vixData || !spyTrend || vixData.status !== 'success' || spyTrend.status !== 'success') {
      // Default regime when data unavailable
      return {
        type: 'STAGNATION',
        label: 'Unknown',
        emoji: '?',
        color: '#6B7280',
        description: 'Market data unavailable. Please try again later.',
      };
    }

    const inputs: MarketInputs = {
      vix: vixData.current_vix || 20,
      priceAboveSma: (spyTrend.percent_above_sma || 0) >= 0,
      percentAboveSma: spyTrend.percent_above_sma || 0,
      slopeDirection: spyTrend.slope_direction ?? null
    };

    return calculateRegime(inputs);
  };

  const regime = getRegime();
  const vix = vixData?.current_vix || 0;
  const isVixLoading = loading || !vixData || vixData.status !== 'success';

  // Notify parent when regime changes (includes full regime info)
  useEffect(() => {
    if (!loading && onRegimeChange) {
      onRegimeChange(regime);
    }
  }, [regime.type, loading, onRegimeChange]);

  return (
    <div className={`market-context-widget${vertical ? ' vertical' : ''}`}>
      {/* Sensor Cards — each renders as its own tile in vertical mode */}
      <div className="market-sensors-row">
        <FearGauge vix={vix} loading={isVixLoading} />
        <MarketMomentum loading={loading} spyTrend={spyTrend} />
        <SectorRotation />
      </div>
    </div>
  );
};

export default MarketContextWidget;
